import React, { useEffect, useState } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const SECTION_CONFIG = {
  painPoints: { icon: '⚠️', color: '#dc3545', bg: '#f8d7da', borderColor: '#f5c6cb' },
  topServices: { icon: '⭐', color: '#1a73e8', bg: '#d1ecf1', borderColor: '#bee5eb' },
  faqs: { icon: '❓', color: '#28a745', bg: '#d4edda', borderColor: '#c3e6cb' },
  opportunities: { icon: '💡', color: '#856404', bg: '#fff3cd', borderColor: '#ffeeba' },
  performanceInsights: { icon: '📈', color: '#6f42c1', bg: '#e8d5f5', borderColor: '#d4b5e8' }
};

function getWeekId(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function formatDateRange(weekStart, weekEnd) {
  if (!weekStart || !weekEnd) return '';
  const start = weekStart.toDate ? weekStart.toDate() : new Date(weekStart);
  const end = weekEnd.toDate ? weekEnd.toDate() : new Date(weekEnd);
  const opts = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(undefined, opts)} ${end.getFullYear()}`;
}

function timeAgo(date) {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : new Date(date);
  const diffMs = Date.now() - d.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return '< 1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export default function InsightsDashboard({ onCreateAd, showPerformanceInsights = false }) {
  const { context } = useOrganization();
  const { t } = useTranslation('adsStudio');
  const [insights, setInsights] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const basePath = context.isOrganization
      ? `organizations/${context.orgId}`
      : `clientes/${uid}`;

    const q = query(
      collection(db, basePath, 'adsInsights'),
      orderBy('weekStart', 'desc'),
      limit(12)
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setInsights(docs);
      setLoading(false);
    }, (err) => {
      console.error('InsightsDashboard error:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [context.isOrganization, context.orgId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'generateInsightsOnDemand', { timeout: 120000 });
      const result = await call({
        organizationId: context.isOrganization ? context.orgId : null
      });
      if (result.data?.rateLimited) {
        setError(t('insights.rateLimited'));
      }
      setSelectedIndex(0);
    } catch (err) {
      console.error('Generate insights error:', err);
      setError(t('insights.error'));
    } finally {
      setGenerating(false);
    }
  };

  const current = insights[selectedIndex] || null;
  const currentWeekId = getWeekId(new Date());

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
        {t('insights.generating')}
      </div>
    );
  }

  // Empty state
  if (insights.length === 0 && !current) {
    return (
      <div style={{
        padding: '4rem 2rem',
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '1px dashed #dee2e6'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
          {t('insights.empty.title')}
        </h3>
        <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.95rem' }}>
          {t('insights.empty.description')}
        </p>
        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: generating ? '#ccc' : '#1a73e8',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: generating ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          {generating ? t('insights.generating') : t('insights.empty.cta')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.3rem' }}>
            {t('insights.title')}
          </h2>
          {current && (
            <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>
              {formatDateRange(current.weekStart, current.weekEnd)}
              {current.generatedAt && (
                <span> — {t('insights.lastUpdate')}: {timeAgo(current.generatedAt)}</span>
              )}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Week navigation */}
          <button
            onClick={() => setSelectedIndex(Math.min(selectedIndex + 1, insights.length - 1))}
            disabled={selectedIndex >= insights.length - 1}
            title={t('insights.previousWeek')}
            style={{
              padding: '0.4rem 0.6rem',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: selectedIndex >= insights.length - 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: selectedIndex >= insights.length - 1 ? 0.5 : 1
            }}
          >
            &#8249;
          </button>
          <span style={{ fontSize: '0.85rem', color: '#666', minWidth: '70px', textAlign: 'center' }}>
            {current?.id || currentWeekId}
          </span>
          <button
            onClick={() => setSelectedIndex(Math.max(selectedIndex - 1, 0))}
            disabled={selectedIndex <= 0}
            title={t('insights.nextWeek')}
            style={{
              padding: '0.4rem 0.6rem',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: selectedIndex <= 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: selectedIndex <= 0 ? 0.5 : 1
            }}
          >
            &#8250;
          </button>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: generating ? '#ccc' : '#1a73e8',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: generating ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              marginLeft: '0.5rem'
            }}
          >
            {generating ? t('insights.generating') : t('insights.generate')}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          border: '1px solid #ffc107',
          color: '#856404',
          marginBottom: '1rem',
          fontSize: '0.9rem'
        }}>
          {error}
        </div>
      )}

      {/* Low data warning */}
      {current && current.messageCount < 5 && !current.error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#d1ecf1',
          borderRadius: '8px',
          border: '1px solid #bee5eb',
          color: '#0c5460',
          marginBottom: '1rem',
          fontSize: '0.9rem'
        }}>
          {t('insights.lowData')}
        </div>
      )}

      {/* Stats bar */}
      {current && (
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          marginBottom: '1.5rem',
          fontSize: '0.85rem',
          color: '#666'
        }}>
          <span>{current.messageCount || 0} {t('insights.messagesAnalyzed')}</span>
        </div>
      )}

      {/* Error state for this insight */}
      {current && current.error && (current.painPoints || []).length === 0 && (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#f8d7da',
          borderRadius: '8px',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          marginBottom: '1rem'
        }}>
          <p style={{ margin: 0 }}>{t('insights.error')}</p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', opacity: 0.8 }}>{current.error}</p>
        </div>
      )}

      {/* Insight sections */}
      {current && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem'
        }}>
          {Object.entries(SECTION_CONFIG).map(([key, config]) => {
            if (key === 'performanceInsights' && !showPerformanceInsights) return null;
            const items = current[key] || [];
            if (items.length === 0 && !current.error) return null;

            return (
              <div key={key} style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '1.25rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: `1px solid ${config.borderColor}`,
                borderTop: `3px solid ${config.color}`
              }}>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>{config.icon}</span>
                  {t(`insights.${key}`)}
                </h3>

                {items.length === 0 ? (
                  <p style={{ margin: 0, color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>—</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyle: 'disc' }}>
                    {items.map((item, i) => (
                      <li key={i} style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.9rem',
                        color: '#333',
                        lineHeight: '1.4'
                      }}>
                        {item}
                        {key === 'opportunities' && (
                          <button
                            onClick={() => onCreateAd ? onCreateAd({ opportunity: item, weekId: current?.id }) : alert(t('insights.createAdSoon'))}
                            style={{
                              display: 'inline-block',
                              marginLeft: '0.5rem',
                              padding: '2px 8px',
                              backgroundColor: config.bg,
                              color: config.color,
                              border: `1px solid ${config.borderColor}`,
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                          >
                            {t('insights.createAd')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
